import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import {
  type Editor,
  type EditorRef,
  type EmailEditorProps as UnlayerEmailEditorProps,
} from "react-email-editor";
import { useGetImageAssets } from "../../assets/hooks/use-get-image-assets";
import type { DisplayConditionReturn } from "../../unlayer-email-content/hooks/use-display-condition";
import { useUploadImage } from "../../unlayer-email-content/hooks/use-upload-image";
import type { UnlayerDesign } from "../../unlayer-email-content/types/unlayer-design";
import type { UnlayerUpload } from "../../unlayer-email-content/types/unlayer-upload";
import { useFollowUpDisplayCondition } from "./use-follow-up-display-condition";

interface UseFollowUpUnlayerEditorOptions {
  initialDesign: UnlayerDesign | null;
  onChange: (design: UnlayerDesign, html: string) => void;
}

export function useFollowUpUnlayerEditor({
  initialDesign,
  onChange,
}: UseFollowUpUnlayerEditorOptions): {
  editor: RefObject<EditorRef | null>;
  dirty: boolean;
  save: () => void;
  onReady: UnlayerEmailEditorProps["onReady"];
  displayCondition: DisplayConditionReturn;
  reset: () => void;
} {
  const editor = useRef<EditorRef>(null);
  const [dirty, setDirty] = useState(false);

  // Use a ref for onChange so the design:updated listener (registered once in onReady)
  // always calls the latest callback, avoiding stale closure issues
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const uploadImage = useUploadImage();
  const displayCondition = useFollowUpDisplayCondition();
  const getImageAssets = useGetImageAssets();

  const onSave = useCallback((html: string, design: UnlayerDesign) => {
    onChangeRef.current(design, html);
  }, []);

  const save = useCallback(() => {
    editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
      onSave(html, emailDesign);
      setDirty(false);
    });
  }, [onSave]);

  const onReady: UnlayerEmailEditorProps["onReady"] = useCallback(
    (unlayer: Editor) => {
      unlayer.registerCallback("displayCondition", displayCondition.onDisplayConditionRequest);

      if (uploadImage) unlayer.registerCallback("image", uploadImage);
      if (initialDesign) unlayer.loadDesign(initialDesign);

      editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
        onSave(html, emailDesign);
        setDirty(false);
      });

      unlayer.addEventListener("design:updated", () => {
        setDirty(true);
        editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
          onSave(html, emailDesign);
          setDirty(false);
        });
      });

      unlayer.registerProvider(
        "userUploads",
        function (
          params: { page: number; searchText: string },
          done: (
            images: UnlayerUpload[],
            pagination: { hasMore: boolean; total: number; page: number; perPage: number },
          ) => void,
        ) {
          const search = params.searchText.trim().toLowerCase();

          void getImageAssets().then((contentLibraryAssets) => {
            const images = contentLibraryAssets
              ?.filter(
                (x) =>
                  !search ||
                  x.title.trim().toLowerCase().includes(search) ||
                  x.content_type?.trim().toLowerCase().includes(search),
              )
              .map((asset) => ({
                id: asset.id,
                location: asset.public_url,
                contentType: asset.mime_type,
                source: "user",
              })) as UnlayerUpload[];

            const page = params.page || 1;
            const perPage = 20;
            const total = images.length;
            const hasMore = total > page * perPage;

            done(images.slice((page - 1) * perPage, page * perPage), {
              hasMore,
              total,
              page,
              perPage,
            });
          });
        },
      );
    },
    [displayCondition, initialDesign, uploadImage, onSave, getImageAssets],
  );

  return {
    editor,
    dirty,
    save,
    onReady,
    displayCondition,
    reset: () => {
      initialDesign && editor?.current?.editor?.loadDesign(initialDesign);
      setDirty(false);
    },
  };
}
